import {
	ChevronLeftIcon,
	GeoPositionIcon,
	MapPinIcon,
	SpinnerIcon,
	XIcon,
} from "@au-schein-de/au-ui-library";
import type { AddressResult } from "@dransay/address-validation";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

/**
 * Props for `MobileAddressAutocomplete`.
 *
 * All optional action rows (geolocation, manual entry) are prop-driven:
 * pass the callback to render the row, omit it to hide it. This keeps the
 * overlay reusable without adding modal-specific variants.
 *
 * @example Delivery modal — geolocation only:
 * ```tsx
 * <MobileAddressAutocomplete
 *   variant="delivery" renderMode="inline"
 *   onRequestLocation={requestLocation} isLocating={isLocating}
 * />
 * ```
 *
 * @example Profile/checkout form — manual entry only (geolocation gated by feature flag):
 * ```tsx
 * <MobileAddressAutocomplete
 *   variant="form" renderMode="portal"
 *   onManualEntryClick={handleManualEntry}
 * />
 * ```
 */
interface MobileAddressAutocompleteProps {
	open: boolean;
	onClose: () => void;
	query: string;
	onQueryChange: (value: string) => void;
	suggestions: AddressResult[];
	onSuggestionSelect: (address: AddressResult) => void;
	/** Controls input deletion behavior: "delivery" clears all on backspace, "form" deletes character-by-character. */
	variant: "delivery" | "form";
	isLoading?: boolean;
	errorMessage?: string | null;
	placeholder?: string;
	/** "inline" mounts within the parent DOM; "portal" mounts on document.body with scroll lock. */
	renderMode: "inline" | "portal";
	/** When provided, renders "Use my current location" row. Omit to hide. */
	onRequestLocation?: () => void;
	/** Shows a loading spinner on the location button when true. */
	isLocating?: boolean;
	/** When provided, renders "Can't find the address?" footer after suggestions or on no results. */
	onManualEntryClick?: () => void;
}

function formatSuggestion(address: AddressResult): string {
	const street = [address.street, address.houseNumber]
		.filter(Boolean)
		.join(" ");
	const location = [address.postalCode, address.city].filter(Boolean).join(" ");
	return [street, location].filter(Boolean).join(", ");
}

/**
 * Fullscreen mobile address autocomplete overlay.
 *
 * Renders action rows based on which callbacks are provided:
 * - `onRequestLocation` → "Use my current location" row
 * - `onManualEntryClick` → "Can't find the address?" footer
 *
 * Used by `DeliveryAddressModal` (inline, delivery variant) and
 * `AddressAutocompleteWrapper` (portal, form variant).
 */
export function MobileAddressAutocomplete({
	open,
	onClose,
	query,
	onQueryChange,
	suggestions,
	onSuggestionSelect,
	variant,
	isLoading = false,
	errorMessage = null,
	placeholder,
	renderMode,
	onRequestLocation,
	isLocating = false,
	onManualEntryClick,
}: Readonly<MobileAddressAutocompleteProps>) {
	const { t } = useTranslation(["common", "auth"]);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!open) {
			return;
		}
		inputRef.current?.focus();
	}, [open]);

	useEffect(() => {
		if (renderMode !== "portal" || !open) {
			return;
		}
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [renderMode, open]);

	if (!open) {
		return null;
	}

	if (renderMode === "portal" && typeof document === "undefined") {
		return null;
	}

	const handleInputChange = (nextValue: string) => {
		if (variant === "delivery") {
			const isDeletion = nextValue.length < query.length;
			onQueryChange(isDeletion ? "" : nextValue);
			return;
		}
		onQueryChange(nextValue);
	};

	const showEmptyState =
		isLoading && suggestions.length === 0 && query.trim().length > 0;

	const content = (
		<div
			className={`bg-white md:hidden ${
				renderMode === "inline"
					? "absolute inset-0 z-10"
					: "fixed inset-0 z-[120]"
			}`}
			data-testid="mobile-address-autocomplete"
		>
			<div className="px-4 pt-3 pb-2">
				<div className="flex h-12 items-center gap-2 rounded-xl border-2 border-ansay-main bg-gray-100 px-3">
					<button
						type="button"
						onClick={onClose}
						data-testid="mobile-address-autocomplete-back"
						aria-label={t("common:back")}
						className="cursor-pointer text-gray-700"
					>
						<ChevronLeftIcon size={6} />
					</button>
					<input
						ref={inputRef}
						data-testid="mobile-address-autocomplete-input"
						className="text-body min-w-0 flex-1 bg-transparent text-gray-700 outline-none placeholder:text-gray-500"
						value={query}
						placeholder={placeholder}
						aria-label={placeholder}
						onChange={(event) => {
							handleInputChange(event.currentTarget.value);
						}}
					/>
					<button
						type="button"
						onClick={() => onQueryChange("")}
						data-testid="mobile-address-autocomplete-clear"
						aria-label={t("common:header.addressModal.clearSearch")}
						className="cursor-pointer text-gray-500"
					>
						<XIcon size={6} />
					</button>
				</div>
			</div>

			{onRequestLocation && (
				<div className="rounded-t-xl border border-gray-200 bg-white px-4 py-4">
					<div className="flex flex-col">
						<button
							type="button"
							className="flex w-full items-start gap-3 text-left cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
							data-testid="mobile-address-autocomplete-use-location"
							onClick={onRequestLocation}
							disabled={isLocating}
						>
							<span
								className="shrink-0 pt-1 text-ansay-main"
								aria-hidden="true"
							>
								{isLocating ? (
									<span className="animate-spin">
										<SpinnerIcon size={6} />
									</span>
								) : (
									<GeoPositionIcon size={6} />
								)}
							</span>
							<span className="flex flex-col gap-0.5">
								<span className="text-body-sm-emphasis text-gray-700">
									{t("common:header.addressModal.useCurrentLocation")}
								</span>
								<span className="text-caption text-gray-500">
									{t("common:header.addressModal.addLater")}
								</span>
							</span>
						</button>
					</div>
				</div>
			)}

			<div className="overflow-y-auto">
				{showEmptyState && (
					<div className="flex items-center justify-center px-4 py-8">
						<span className="animate-spin text-ansay-main" aria-hidden="true">
							<SpinnerIcon size={6} />
						</span>
					</div>
				)}

				{errorMessage && !showEmptyState && !onManualEntryClick && (
					<p className="px-4 py-8 text-center text-body text-gray-500">
						{errorMessage}
					</p>
				)}

				{suggestions.map((address, index) => (
					<button
						key={`${address.id}-${index}`}
						type="button"
						className="text-body block w-full border-b border-gray-200 px-4 py-3 text-left text-gray-700 cursor-pointer hover:bg-gray-50"
						data-testid={`mobile-address-autocomplete-suggestion-${index}`}
						onClick={() => {
							onSuggestionSelect(address);
						}}
					>
						{formatSuggestion(address)}
					</button>
				))}

				{onManualEntryClick &&
					(suggestions.length > 0 ||
						(errorMessage && suggestions.length === 0)) && (
						<button
							type="button"
							className="flex w-full items-start gap-3 border-t border-gray-200 bg-gray-50 p-3 text-left cursor-pointer hover:bg-gray-100"
							data-testid="mobile-address-autocomplete-manual-entry"
							onClick={onManualEntryClick}
						>
							<div className="shrink-0 text-ansay-main">
								<MapPinIcon size={6} />
							</div>
							<div className="flex flex-col gap-0.5">
								<span className="text-body-sm-emphasis text-gray-700">
									{t("auth:register.cantFindAddress")}
								</span>
								<span className="text-caption text-gray-500">
									{t("auth:register.typeManually")}
								</span>
							</div>
						</button>
					)}
			</div>
		</div>
	);

	if (renderMode === "portal") {
		return createPortal(content, document.body);
	}

	return content;
}
